// Onda 2 (2026-06-02): migrado pra Antd — usa Tag, Button, Space.
// E o conteudo encaixa em <Timeline.Item> via cor do dot.
import { Space, Tag, Button, Typography, Popconfirm } from 'antd';
import { CheckOutlined, ReloadOutlined, DeleteOutlined, CloseOutlined } from '@ant-design/icons';
import { fmtDate, fmtDateTime, CANAIS_INTERACAO, STATUS_LABELS, metaDoEvento } from './utils';

const TIPO_META: Record<string, { emoji: string; cor: string; label: string }> = {
  nota:              { emoji: '💬', cor: '#0930D0', label: 'Interação' },
  status_alterado:   { emoji: '🔄', cor: '#7C3AED', label: 'Status alterado' },
  composicao_criada: { emoji: '📊', cor: '#0a7f4a', label: 'Composição' },
  proposta_gerada:   { emoji: '📄', cor: '#B07500', label: 'Proposta gerada' },
  aceite_digital:    { emoji: '✓',  cor: '#0a7f4a', label: 'Aceite digital' },
  negocio_criado:    { emoji: '🎯', cor: '#888',    label: 'Negócio criado' },
  atividade:         { emoji: '📌', cor: '#B07500', label: 'Atividade' },
  // 01/10/2026: os tipos mais frequentes em produção apareciam com o nome cru
  // ("venda_gerada", "proposta_publicada") e uma bolinha.
  venda_gerada:       { emoji: '🧾', cor: '#0a7f4a', label: 'Venda gerada' },
  revisao_criada:     { emoji: '📝', cor: '#B07500', label: 'Revisão da proposta' },
  proposta_publicada: { emoji: '🔗', cor: '#0930D0', label: 'Proposta publicada' },
  creditos_definidos: { emoji: '👥', cor: '#4A6285', label: 'Créditos definidos' },
  anexo_adicionado:   { emoji: '📎', cor: '#4A6285', label: 'Anexo' },
  anexo_removido:     { emoji: '📎', cor: '#888',    label: 'Anexo removido' },
  anexos_dwight:      { emoji: '📎', cor: '#4A6285', label: 'Anexos (Dwight)' },
  cliente_alterado:   { emoji: '🏢', cor: '#4A6285', label: 'Empresa alterada' },
  data_venda_alterada: { emoji: '🗓', cor: '#B07500', label: 'Data da venda alterada' },
  movido_para_leads:  { emoji: '↩️', cor: '#888',    label: 'Voltou para Leads' },
};

/** Tipo sem rótulo próprio vira frase ("assinatura_pdf_gerado" → "Assinatura pdf gerado"). */
function rotuloDeTipo(tipo: string): string {
  const t = String(tipo || '').replace(/_/g, ' ').trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : 'Evento';
}

export function EventoItem({ evento, onExcluir, onConcluirAtividade, onReabrirAtividade, onExcluirAtividade }: {
  evento: any;
  onExcluir: (() => void) | null;
  onConcluirAtividade?: (id: string) => void;
  onReabrirAtividade?: (id: string) => void;
  onExcluirAtividade?: (id: string) => void;
}) {
  const meta = metaDoEvento(evento);
  const canal = evento.tipo === 'nota' && meta?.canal
    ? CANAIS_INTERACAO.find(c => c.chave === meta.canal) : null;
  const cfg = canal
    ? { emoji: canal.emoji, cor: '#0930D0', label: canal.rotulo }
    : TIPO_META[evento.tipo] || { emoji: '•', cor: '#888', label: rotuloDeTipo(evento.tipo) };
  // "Status alterado de "follow_up" para "negociacao"" → o nome das etapas que a
  // pessoa conhece. O texto gravado não muda; só a leitura.
  const descricao = evento.tipo === 'status_alterado' && meta?.de && meta?.para
    ? `Etapa: ${STATUS_LABELS[meta.de] || meta.de} → ${STATUS_LABELS[meta.para] || meta.para}`
    : evento.descricao;
  const ativ = evento.atividade;
  const concluida = ativ && (ativ.status === 'concluida' || ativ.status === 'cancelada');
  const venc = ativ?.data_vencimento ? new Date(ativ.data_vencimento) : null;
  const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
  const atrasada = venc && venc < hoje && !concluida;

  let bolhaCor = cfg.cor;
  let bolhaIcone = cfg.emoji;
  if (ativ) {
    if (concluida) { bolhaCor = '#0a7f4a'; bolhaIcone = '✓'; }
    else if (atrasada) { bolhaCor = '#B91626'; }
  }

  return (
    <div style={{
      display: 'flex', gap: 10, padding: '10px 4px',
      borderBottom: '0.5px solid #f0f0f0',
    }}>
      <div style={{
        flex: '0 0 30px', width: 30, height: 30, borderRadius: 15,
        background: `${bolhaCor}22`, color: bolhaCor,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 14, fontWeight: 600,
      }}>{bolhaIcone}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Typography.Text style={{
          fontSize: 12, color: '#222', whiteSpace: 'pre-wrap',
          textDecoration: concluida ? 'line-through' : 'none',
          opacity: concluida ? 0.6 : 1,
        }}>
          {descricao}
        </Typography.Text>
        {ativ?.descricao && (
          <div style={{ fontSize: 11, color: '#666', marginTop: 3, whiteSpace: 'pre-wrap',
            opacity: concluida ? 0.55 : 1 }}>
            {ativ.descricao}
          </div>
        )}
        <Space size={6} wrap style={{ marginTop: 4 }}>
          <Tag color={cfg.cor} bordered={false}>{cfg.label}</Tag>
          {ativ?.tipo && <Tag bordered={false}>📌 {ativ.tipo}</Tag>}
          {ativ?.prioridade && (
            <Tag bordered={false} color={
              ativ.prioridade === 'urgente' ? 'error'
              : ativ.prioridade === 'alta' ? 'warning' : 'default'
            }>{ativ.prioridade}</Tag>
          )}
          {venc && (
            <Tag bordered={false} color={atrasada ? 'error' : 'default'}>
              {atrasada ? '⏰ atrasada' : '🗓'} {fmtDate(ativ.data_vencimento)}
            </Tag>
          )}
          {!ativ && (
            <Typography.Text type="secondary" style={{ fontSize: 10 }}>
              · {fmtDateTime(evento.criado_em)}
            </Typography.Text>
          )}
          {ativ && concluida && (
            <Typography.Text type="secondary" style={{ fontSize: 10 }}>
              concluída em {fmtDate(ativ.data_conclusao)}
            </Typography.Text>
          )}
          {evento.criado_por && (
            <Typography.Text type="secondary" style={{ fontSize: 10 }}>
              · {evento.criado_por}
            </Typography.Text>
          )}
        </Space>
        {ativ && Array.isArray(ativ.atribuicoes) && ativ.atribuicoes.length > 0 && (
          <div style={{ fontSize: 10, color: '#666', marginTop: 3 }}>
            👤 {ativ.atribuicoes.map((a: any) => a.email).join(', ')}
          </div>
        )}
        {ativ && (
          <Space size={6} style={{ marginTop: 6 }}>
            {!concluida ? (
              <Button size="small" icon={<CheckOutlined />}
                onClick={() => onConcluirAtividade?.(ativ.id)}
                style={{ color: '#0a7f4a', borderColor: '#0a7f4a' }}>
                Concluir
              </Button>
            ) : (
              <Button size="small" icon={<ReloadOutlined />}
                onClick={() => onReabrirAtividade?.(ativ.id)}>
                Reabrir
              </Button>
            )}
            <Popconfirm title="Excluir atividade?" okText="Excluir" okType="danger" cancelText="Cancelar"
              onConfirm={() => onExcluirAtividade?.(ativ.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        )}
      </div>
      {onExcluir && (
        <Popconfirm title="Excluir este evento?" okText="Excluir" okType="danger" cancelText="Cancelar"
          onConfirm={onExcluir}>
          <Button size="small" type="text" icon={<CloseOutlined />} title="Excluir evento" />
        </Popconfirm>
      )}
    </div>
  );
}
